import { useCallback, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { useAuthStore } from '@/store';
import { openRemoteController } from '@/lib/remoteController';

// Check the chat's own Mac, including local chats that have never been attached
// to a Remote reservation. Presence does not grant access; sends still verify it.
export default function useRemoteMacConnection({ enabled, hostId, deviceId }) {
  const userId = useAuthStore(state => state.user?.id);
  const generation = useAuthStore(state => state._sessionGeneration);
  const signedIn = useAuthStore(state => !!state.token && !state._isSigningOut);
  const mobile = Platform.OS === 'ios' || Platform.OS === 'android';
  const needsPresence = mobile && enabled && signedIn && !!userId;
  const key = JSON.stringify([userId, generation, hostId, deviceId]);
  const [connection, setConnection] = useState(null);

  useFocusEffect(useCallback(() => {
    setConnection(null);
    if (!needsPresence || (!hostId && !deviceId)) return undefined;
    let alive = true, foreground = AppState.currentState !== 'background' && AppState.currentState !== 'inactive';
    let timer, checking;
    const cancel = () => {
      clearTimeout(timer);
      const request = checking;
      checking = null;
      request?.session?.stop();
      if (request) request.session = null;
    };
    const check = async () => {
      if (!alive || !foreground || checking) return;
      const request = {};
      checking = request;
      const current = () => {
        const auth = useAuthStore.getState();
        return alive && foreground && checking === request && !!auth.token && !auth._isSigningOut &&
          auth.user?.id === userId && auth._sessionGeneration === generation;
      };
      try {
        request.session = openRemoteController({ background: true, isRequestCurrent: current });
        const hosts = await request.session.refresh();
        if (current()) {
          const host = hosts?.find(item => hostId ? item.id === hostId : item.deviceId === deviceId);
          setConnection({ key, status: host?.online && host.enabled ? 'connected' : 'disconnected' });
        }
      } catch {
        if (current()) setConnection({ key, status: 'disconnected' });
      } finally {
        request.session?.stop();
        if (current()) {
          checking = null;
          timer = setTimeout(check, 15000);
        }
      }
    };
    check();
    const subscription = AppState.addEventListener('change', state => {
      foreground = state === 'active';
      cancel();
      setConnection(null);
      if (foreground) check();
    });
    return () => { alive = false; cancel(); subscription.remove(); setConnection(null); };
  }, [needsPresence, userId, generation, hostId, deviceId, key]));

  if (!needsPresence) return null;
  return connection?.key === key ? connection.status : 'checking';
}
